import { trackEvent, trackProps } from '../../helpers/utils/analytics';
import {
  lazy,
  startTransition,
  Suspense,
  useState,
  useContext,
  useEffect,
  useRef,
  useId,
} from 'react';
import type { ReactNode } from 'react';
import { Flex, Box, Text, IconButton, Stack } from './';
import {
  Settings,
  Download,
  Info,
  Mail,
  RotateCcw,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react';
import SpadeLogo from './SpadeLogo';
import { pressDim, pressWash } from './press';
import { usePWAInstall } from '../../helpers/utils/usePWAInstall';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadAboutPage } from '../../pages/infoRoutes';
import { GlobalContext } from '../../store/global-context';
import { getNames } from '../../helpers/utils/storage';
import { initialNames } from '../../helpers/utils/constants';
import { getPersistedViewerSeat } from '../../helpers/utils/viewerSession';
import {
  hasPlayerNamesEntered,
  hasRoundProgress,
} from '../../helpers/math/spadesMath';
import ShareTip from '../realtime/ShareTip';
import WatchingChip from '../realtime/WatchingChip';
import { takeShareRetry } from '../../firebase/loadRealtime';
import { useLeaveSession } from '../realtime/useLeaveSession';
import {
  useMediaQuery,
  useScreenLayout,
} from '../../helpers/utils/useScreenLayout';
import {
  DESKTOP_HEADER_ZOOM,
  TABLET_HEADER_ZOOM,
  HEADER_FITS_ALL_QUERY,
  HEADER_FITS_ABOUT_QUERY,
} from '../../helpers/utils/screenFit';

const loadSettingsModal = () => import('../modals/SettingsModal');
const loadContactModal = () => import('../modals/ContactModal');
const loadWarningModal = () => import('../modals/WarningModal');
const loadShareWatch = () => import('../realtime/ShareWatch');
const loadViewerControls = () => import('../realtime/ViewerControls');
const SettingsModal = lazy(loadSettingsModal);
const ContactModal = lazy(loadContactModal);
const WarningModal = lazy(loadWarningModal);
const ShareWatch = lazy(loadShareWatch);
const ViewerControls = lazy(loadViewerControls);

const MENU_WIDTH = 222;
const MENU_ROW_HEIGHT = 48;
/** Centres the 44pt menu slot in the wide header's 63pt bar. */
const DESKTOP_MENU_TOP = '9.5px';
/** The wide header's right padding, so the menu's edge meets the slot's. */
const DESKTOP_MENU_RIGHT = '20px';

interface MenuItem {
  key: string;
  icon: ReactNode;
  label: string;
  /** The desktop header can't fit it at this width, so it goes in the ☰. */
  overflowsDesktop?: boolean;
  onClick: () => void;
  track?: ReturnType<typeof trackProps>;
  prefetch?: () => void;
}

// Emerald/Header menu "Tab + panel shape" (1580:20213): one outline that wraps
// the 44pt menu slot (the tab) and the rows below it. Figma drew the 3- and
// 4-row heights; the path here is the same geometry with the bottom edge
// following the row count, so the 2-row menus (installed viewer) fit too.
const MenuTabShape = ({ height }: { height: number }) => {
  const gradientId = useId();
  const bottom = height - 0.75;
  const path = [
    'M190 2.75 H210',
    'A11.25 11.25 0 0 1 221.25 14',
    `V${bottom - 11.25}`,
    `A11.25 11.25 0 0 1 210 ${bottom}`,
    `H12`,
    `A11.25 11.25 0 0 1 0.75 ${bottom - 11.25}`,
    'V56',
    'A11.25 11.25 0 0 1 12 44.75',
    'H170',
    'A8.75 8.75 0 0 0 178.75 36',
    'V14',
    'A11.25 11.25 0 0 1 190 2.75 Z',
  ].join(' ');
  return (
    <svg
      aria-hidden="true"
      width={MENU_WIDTH}
      height={height}
      viewBox={`0 0 ${MENU_WIDTH} ${height}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        overflow: 'visible',
        filter: 'drop-shadow(0 16px 40px rgba(0, 0, 0, 0.6))',
      }}
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="2"
          x2="0"
          y2={height}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--spades-panel-top)" />
          <stop offset="1" stopColor="var(--spades-surface)" />
        </linearGradient>
      </defs>
      <path
        d={path}
        fill={`url(#${gradientId})`}
        stroke="var(--spades-border-strong)"
        strokeWidth="1.5"
      />
    </svg>
  );
};

const HeaderMenu = ({
  id,
  items,
  top,
  right,
  onDismiss,
}: {
  id: string;
  items: MenuItem[];
  top: string;
  right: string;
  onDismiss: () => void;
}) => {
  // Rows start 44pt down (below the menu slot) with 6pt above and below.
  const height = 44 + 12 + items.length * MENU_ROW_HEIGHT + (items.length - 1);
  return (
    <>
      <Box
        position="fixed"
        top="0"
        left="0"
        w="100vw"
        h="100vh"
        zIndex="100"
        {...trackProps('menu_closed')}
        onClick={onDismiss}
      />
      <Box
        id={id}
        data-testid="header-menu"
        position="absolute"
        top={top}
        right={right}
        w={`${MENU_WIDTH}px`}
        h={`${height}px`}
        zIndex="105"
      >
        <MenuTabShape height={height} />
        <Stack gap={0} position="relative" pt="44px" pb="6px">
          {items.map((item, index) => (
            <Box key={item.key}>
              {index > 0 && (
                <Box pl="48px" pr="16px">
                  <Box h="1px" bg="var(--spades-border)" />
                </Box>
              )}
              <Flex
                as="button"
                // @ts-expect-error Flex renders a button here
                type="button"
                width="100%"
                bg="transparent"
                border="0"
                textAlign="left"
                color="spades.ivory"
                _focusVisible={{
                  outline: '2px solid var(--spades-accent-base)',
                  outlineOffset: '-2px',
                }}
                data-menu-row=""
                h={`${MENU_ROW_HEIGHT}px`}
                px="16px"
                align="center"
                gap="12px"
                cursor="pointer"
                _hover={{ bg: 'whiteAlpha.100' }}
                css={pressWash}
                {...item.track}
                onPointerDown={() => item.prefetch?.()}
                onClick={item.onClick}
              >
                {item.icon}
                <Text
                  fontSize="var(--spades-font-size-sm)"
                  fontWeight="700"
                  lineHeight={1.25}
                  color="spades.ivory"
                  whiteSpace="nowrap"
                >
                  {item.label}
                </Text>
              </Flex>
            </Box>
          ))}
        </Stack>
      </Box>
    </>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const layout = useScreenLayout();
  const fitsAllActions = useMediaQuery(HEADER_FITS_ALL_QUERY);
  const fitsAbout = useMediaQuery(HEADER_FITS_ABOUT_QUERY);
  const {
    role,
    isViewerSynced,
    roundHistory,
    currentRound,
    setRoundHistory,
    resetCurrentRound,
  } = useContext(GlobalContext);
  // Warm common dialogs after the header is usable, so the first menu tap
  // does not need to wait for their code to download and execute.
  useEffect(() => {
    const preload = () => {
      void Promise.all([
        loadSettingsModal(),
        loadContactModal(),
        loadWarningModal(),
        loadAboutPage(),
        import('../modals/DealerSelectionModal'),
        import('../modals/ScoreLimitModal'),
      ]).catch(() => undefined);
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(preload, { timeout: 2000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(preload, 1000);
    return () => window.clearTimeout(id);
  }, []);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuId = useId();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  // A resize can change what the menu holds, so it doesn't stay open through
  // one.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [layout, fitsAllActions, fitsAbout]);
  // Escape dismisses the menu like its backdrop does, and puts focus back on
  // the button: a focused menu row is about to unmount.
  useEffect(() => {
    if (!isMenuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsMenuOpen(false);
      trackEvent('menu_closed');
      menuButtonRef.current?.focus();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isMenuOpen]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsMounted, setSettingsMounted] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [contactMounted, setContactMounted] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [warningMounted, setWarningMounted] = useState(false);
  // Share's "Try again" after a failed download reloads the page and comes
  // back here open (firebase/loadRealtime.ts).
  const [isShareWatchOpen, setIsShareWatchOpen] = useState(takeShareRetry);
  const [shareMounted, setShareMounted] = useState(isShareWatchOpen);
  const [isWatchingOpen, setIsWatchingOpen] = useState(false);
  // Auto-open the seat picker the first time a viewer's live board mounts.
  // The connecting screen also renders this Header, before sync, and must
  // not prompt. A viewer who already chose a seat (persisted) is NOT
  // re-prompted. The live tree mounts a fresh Header once sync lands, so
  // this initializer still runs once for the board.
  const viewerNeedsSeat =
    role === 'viewer' && isViewerSynced && !getPersistedViewerSeat();
  const [isViewerSeatOpen, setIsViewerSeatOpen] = useState(viewerNeedsSeat);
  const [viewerMounted, setViewerMounted] = useState(viewerNeedsSeat);
  const initialSeatReported = useRef(false);
  useEffect(() => {
    if (isViewerSeatOpen && !initialSeatReported.current) {
      trackEvent('viewer_seat_picker_opened', { source: 'automatic' });
    }
    initialSeatReported.current = true;
  }, [isViewerSeatOpen]);
  const { handleInstallClick, isInstalled } = usePWAInstall();
  const leaveSession = useLeaveSession();

  const names = getNames() ?? initialNames;
  const hasAnyData =
    hasPlayerNamesEntered(names) ||
    hasRoundProgress(roundHistory, currentRound);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsWatchingOpen(false);
    void loadSettingsModal();
  };

  // The watching menu and the hamburger menu must never be open at once — the
  // hamburger dropdown sits later in the DOM and would paint over the watching
  // dropdown. Opening one closes the other.
  const toggleWatching = () => {
    setIsWatchingOpen((open) => !open);
    setIsMenuOpen(false);
  };

  // The modals are lazy: mounting one in a transition keeps the screen as it
  // is until its code is in, instead of showing the Suspense fallback, which
  // React then holds for 300ms before the modal can appear. The menu still
  // closes at once.
  const handleContactClick = () => {
    startTransition(() => {
      setContactMounted(true);
      setIsContactOpen(true);
    });
    setIsMenuOpen(false);
  };

  const handleAboutClick = () => {
    // The new page brings its own header, so the menu leaves with the old one.
    // Closing it first would show the old page by itself until the new arrives.
    if (pathname === '/about') setIsMenuOpen(false);
    else navigate('/about');
  };

  const handleSettingsClick = () => {
    startTransition(() => {
      setSettingsMounted(true);
      setIsSettingsOpen(true);
    });
    setIsMenuOpen(false);
  };

  const handleDownloadClick = () => {
    handleInstallClick();
    setIsMenuOpen(false);
  };

  const handleShareWatchClick = () => {
    startTransition(() => {
      setShareMounted(true);
      setIsShareWatchOpen(true);
    });
    setIsMenuOpen(false);
  };

  const handleViewerSeatClick = () => {
    startTransition(() => {
      setViewerMounted(true);
      setIsViewerSeatOpen(true);
    });
    setIsMenuOpen(false);
    setIsWatchingOpen(false);
    trackEvent('viewer_seat_picker_opened', { source: 'menu' });
  };

  const handleLeaveSession = () => {
    setIsWatchingOpen(false);
    leaveSession();
  };

  const handleNewGameClick = () => {
    if (hasAnyData) {
      startTransition(() => {
        setWarningMounted(true);
        setIsWarningModalOpen(true);
      });
    } else {
      // If no data to clear, just ensure everything is reset and stay on/go to home
      setRoundHistory([]);
      resetCurrentRound();
      trackEvent('game_reset', { source: 'header', teams: 'different' });
      navigate('/');
    }
    setIsMenuOpen(false);
  };

  const handleNavigateHome = () => {
    // A viewer is read-only: the title must not boot them off the live board
    // to the name form. The landing page keeps its own redirect as a failsafe
    // for direct/stale URLs, but a deliberate click here shouldn't navigate.
    if (role === 'viewer') return;
    navigate('/');
  };

  const menuItems: MenuItem[] = [
    role !== 'viewer' && {
      key: 'new-game',
      icon: <RotateCcw size={20} />,
      label: 'New Game',
      onClick: handleNewGameClick,
      prefetch: loadWarningModal,
      track: trackProps('new_game_requested', { source: 'header' }),
    },
    role !== 'viewer' && {
      key: 'share',
      icon: <UserPlus size={20} />,
      label: 'Invite to Game',
      onClick: handleShareWatchClick,
      prefetch: loadShareWatch,
    },
    role === 'viewer' && {
      key: 'switch-seat',
      icon: <UserCheck size={20} />,
      label: 'Switch seat',
      onClick: handleViewerSeatClick,
      prefetch: loadViewerControls,
    },
    !isInstalled && {
      key: 'download',
      icon: <Download size={20} />,
      label: 'Offline Download',
      onClick: handleDownloadClick,
      track: trackProps('install_requested', { source: 'header' }),
    },
    {
      key: 'settings',
      icon: <Settings size={20} />,
      label: 'Settings',
      onClick: handleSettingsClick,
      prefetch: loadSettingsModal,
      track: trackProps('settings_opened'),
    },
    {
      key: 'about',
      icon: <Info size={20} />,
      label: 'About',
      // Without Offline Download, the rest fits at every desktop width.
      overflowsDesktop: !isInstalled && !fitsAbout,
      onClick: handleAboutClick,
      prefetch: loadAboutPage,
      track: trackProps('about_opened'),
    },
    {
      key: 'contact',
      icon: <Mail size={20} />,
      label: 'Contact',
      overflowsDesktop: !isInstalled && !fitsAllActions,
      onClick: handleContactClick,
      prefetch: loadContactModal,
      track: trackProps('feedback_support_opened'),
    },
  ].filter(Boolean) as MenuItem[];
  const desktopMenuItems = menuItems.filter((item) => item.overflowsDesktop);
  const brand = (
    <Flex
      as="button"
      // @ts-expect-error Flex renders a button here
      type="button"
      disabled={role === 'viewer'}
      align="center"
      gap="6px"
      flex="0 1 auto"
      minWidth={0}
      h="44px"
      // An 8px tap buffer each side that takes no layout room: at 1024 the
      // desktop actions leave the wordmark no spare width to give up.
      px="8px"
      mx="-8px"
      bg="transparent"
      border="0"
      overflow="hidden"
      cursor={role === 'viewer' ? 'default' : 'pointer'}
      css={pressDim}
      {...(role !== 'viewer' ? trackProps('home_opened') : {})}
      onClick={handleNavigateHome}
    >
      <SpadeLogo />
      <Text
        as="span"
        fontFamily="spades.brand"
        fontSize="var(--spades-font-size-md)"
        fontWeight="500"
        lineHeight={1.45}
        letterSpacing="-0.54px"
        color="spades.ivory"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        SpadesCalculator
      </Text>
    </Flex>
  );

  const menuButton = (
    <IconButton
      variant="ghost"
      {...trackProps(isMenuOpen ? 'menu_closed' : 'menu_opened')}
      onClick={toggleMenu}
      ref={menuButtonRef}
      aria-label="Open Menu"
      aria-expanded={isMenuOpen}
      aria-controls={isMenuOpen ? menuId : undefined}
      flexShrink={0}
      w="44px"
      h="44px"
      minW="44px"
      minH="44px"
      p={0}
      _hover={{ bg: 'transparent' }}
    >
      {isMenuOpen ? (
        <X size={20} />
      ) : (
        <svg
          aria-hidden="true"
          width="20"
          height="12"
          viewBox="0 0 20 12"
          fill="var(--spades-ivory)"
        >
          {[0, 5, 10].map((y) => (
            <rect key={y} y={y} width="20" height="2" rx="1" />
          ))}
        </svg>
      )}
    </IconButton>
  );

  // One boundary, always mounted, so a modal mounted in a transition waits
  // for its code here instead of showing a fallback.
  const modals = (
    <Suspense fallback={null}>
      {settingsMounted && (
        <SettingsModal isOpen={isSettingsOpen} setIsOpen={setIsSettingsOpen} />
      )}
      {contactMounted && (
        <ContactModal isOpen={isContactOpen} onClose={setIsContactOpen} />
      )}
      {warningMounted && (
        <WarningModal
          isOpen={isWarningModalOpen}
          setIsModalOpen={setIsWarningModalOpen}
        />
      )}
      {shareMounted && (
        <ShareWatch
          isOpen={isShareWatchOpen}
          onClose={() => setIsShareWatchOpen(false)}
        />
      )}
      {viewerMounted && (
        <ViewerControls
          isOpen={isViewerSeatOpen}
          onClose={() => setIsViewerSeatOpen(false)}
        />
      )}
      <ShareTip onShare={handleShareWatchClick} onPrefetch={loadShareWatch} />
    </Suspense>
  );

  // W1 Emerald/Header · wide (1728:20679), >= 1024: the ☰ menu becomes
  // inline actions with the same rows, icons, labels and analytics, as 40pt
  // quiet buttons. Hover = surface fill + border stroke. 64pt tall, x1.12.
  // What doesn't fit at this width stays in a ☰ at the end of the row.
  if (layout === 'desktop') {
    return (
      <Box
        as="header"
        width="100%"
        position="relative"
        zIndex={100}
        flexShrink={0}
        bg="var(--spades-background)"
        borderBottom="1px solid var(--spades-border)"
        style={{ zoom: DESKTOP_HEADER_ZOOM }}
      >
        <Flex
          justify="space-between"
          align="center"
          position="relative"
          zIndex={110}
          // 63 + the 1pt border = Figma's 64 (71.68 at x1.12).
          h="63px"
          pl="32px"
          pr="20px"
          gap="6px"
        >
          {brand}
          <Flex align="center" gap="6px" flexShrink={0}>
            {role === 'viewer' && isViewerSynced && (
              <WatchingChip
                isOpen={isWatchingOpen}
                onToggle={toggleWatching}
                onSwitchSeat={handleViewerSeatClick}
                onLeave={handleLeaveSession}
              />
            )}
            <Flex as="nav" aria-label="Game actions" align="center" gap="3px">
              {menuItems
                .filter((item) => !item.overflowsDesktop)
                .map((item) => (
                  <Flex
                    as="button"
                    key={item.key}
                    // @ts-expect-error Flex renders a button here
                    type="button"
                    h="40px"
                    px="12px"
                    gap="8px"
                    align="center"
                    borderRadius="var(--spades-radius-control)"
                    border="1px solid transparent"
                    bg="transparent"
                    color="spades.ivory"
                    cursor="pointer"
                    _hover={{
                      bg: 'var(--spades-surface)',
                      borderColor: 'var(--spades-border)',
                    }}
                    css={pressWash}
                    _focusVisible={{
                      outline: '2px solid var(--spades-accent-base)',
                      outlineOffset: '2px',
                    }}
                    {...item.track}
                    onPointerDown={() => item.prefetch?.()}
                    onClick={item.onClick}
                  >
                    {item.icon}
                    <Text
                      as="span"
                      fontSize="var(--spades-font-size-sm)"
                      fontWeight="700"
                      lineHeight={1.25}
                      whiteSpace="nowrap"
                    >
                      {item.label}
                    </Text>
                  </Flex>
                ))}
              {desktopMenuItems.length > 0 && menuButton}
            </Flex>
          </Flex>
        </Flex>
        {isMenuOpen && desktopMenuItems.length > 0 && (
          <HeaderMenu
            id={menuId}
            items={desktopMenuItems}
            top={DESKTOP_MENU_TOP}
            right={DESKTOP_MENU_RIGHT}
            onDismiss={() => setIsMenuOpen(false)}
          />
        )}
        {modals}
      </Box>
    );
  }

  return (
    <Box
      as="header"
      width="100%"
      py={0}
      position="relative"
      zIndex={100}
      // T1: the phone header x1.2 on tablets.
      style={layout === 'tablet' ? { zoom: TABLET_HEADER_ZOOM } : undefined}
    >
      <Flex
        justify="space-between"
        align="center"
        position="relative"
        zIndex={110}
        h="44px"
        // The page gutter is 8pt; Figma's header sits 16pt in, with the menu
        // slot 4pt from the screen edge.
        pl="8px"
        mr="-4px"
        gap="6px"
      >
        {brand}
        <Flex align="center" gap="6px" flexShrink={0}>
          {role === 'viewer' && isViewerSynced && (
            <WatchingChip
              isOpen={isWatchingOpen}
              onToggle={toggleWatching}
              onSwitchSeat={handleViewerSeatClick}
              onLeave={handleLeaveSession}
            />
          )}
          {menuButton}
        </Flex>
      </Flex>

      {isMenuOpen && (
        <HeaderMenu
          id={menuId}
          items={menuItems}
          top="0"
          right="-4px"
          onDismiss={() => setIsMenuOpen(false)}
        />
      )}

      {modals}
    </Box>
  );
};

export default Header;
